import { bakeArPillow, DEFAULT_AR_BAKE, exportArGlb, exportArUsdz, loadArInputs } from "./arBake";
import { type ArParkedUrls, park } from "./arDelivery";

// The client-side AR launch orchestration (ADR-0035 §2/§3, issue #382): the glue
// that turns the CURRENT object config into an OS-AR-ready delivery, and lazily
// loads the `<model-viewer>` launcher. It composes the already-shipped, separately
// tested halves — the bake (#380, three) and the byte-park (#381, network) — so
// this module stays a thin, testable seam and `ArEntryButton` owns only React.

// The CURRENT object config the bake flattens (ADR-0035 §7): the chosen mesh, the
// pet texture, the composited Background, and the physical size (→ metre scale).
// The preview gathers this from its live state AT TAP TIME; transient camera state
// (pan/orbit/zoom) is deliberately absent — the OS re-places the object in a
// canonical rest pose.
export interface ArSource {
  // The chosen pillow mesh (per-job silhouette GLB or a static rectangle GLB).
  meshUrl: string;
  // `texture_front` (pet-with-alpha); omit for a bare white pillow.
  textureFrontUrl?: string;
  // The opaque Background composited under the pet into one baseColor (white in the
  // MVP, but wired from the live config — never a constant, matching the viewer).
  background: string;
  // The longest printed FACE side in cm — the true real-world size the AR model is
  // placed at (from sizeModel's readout; ADR-0035 §5).
  targetLongestCm: number;
}

// Bake the current config into GLB + USDZ and park BOTH at real, fetchable `https`
// URLs (never a `blob:` — the OS AR engines run out-of-process, ADR-0035 §3).
// Returns `{ glbUrl, usdzUrl }` for `<model-viewer>` (`src` / `ios-src`). Both
// formats are produced so one launcher serves both platforms; any failure (load /
// export / park) rejects so `ArEntryButton` falls back to idle + a retry toast. The
// whole path is WebGL-free — three's exporters run headless.
export async function bakeAndParkAr(source: ArSource): Promise<ArParkedUrls> {
  const inputs = await loadArInputs({
    meshUrl: source.meshUrl,
    textureFrontUrl: source.textureFrontUrl,
  });
  // Reflect the live config on top of the tuned look defaults (#385 tunes the LOOK
  // in one place — DEFAULT_AR_BAKE — never here).
  const baked = bakeArPillow(inputs, {
    ...DEFAULT_AR_BAKE,
    background: source.background,
    targetLongestCm: source.targetLongestCm,
  });
  const [glb, usdz] = await Promise.all([exportArGlb(baked.object), exportArUsdz(baked.object)]);
  return park({ glb, usdz });
}

// Lazy-load Google's `<model-viewer>` — the SELF-CONTAINED dist build that carries
// its OWN three.js — ONLY on tap, so it (and its three) never enters the main
// bundle (ADR-0035 §3; it is a dynamic-import chunk, verified against
// `dist/stats.html`). Importing the module registers the custom element as a side
// effect. Idempotent: once `model-viewer` is defined, resolve immediately.
export async function loadModelViewer(): Promise<void> {
  if (typeof customElements !== "undefined" && customElements.get("model-viewer")) return;
  await import("@google/model-viewer/dist/model-viewer.min.js");
  if (typeof customElements !== "undefined") {
    await customElements.whenDefined("model-viewer");
  }
}
